import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import { getGitHubStats } from '../../../services/githubService';
import SkillsVisualization from './SkillsVisualization';

const GitHubActivity = () => {
  const [selectedRepo, setSelectedRepo] = useState(null);
  const [activityFilter, setActivityFilter] = useState('all');

  const skills = [
    { name: 'React', level: 95 },
    { name: 'Node.js', level: 92 },
    { name: 'TypeScript', level: 90 },
    { name: 'GraphQL', level: 85 },
    { name: 'Docker', level: 80 },
    { name: 'AWS', level: 75 },
    { name: 'CI/CD', level: 90 },
    { name: 'AI/ML', level: 80 },
    { name: 'Open Source', level: 95 }
  ];

  const [githubStats, setGitHubStats] = useState({
    totalRepos: 0,
    totalStars: 0,
    totalCommits: 0,
    contributionStreak: 0,
    languageStats: [],
  });
  const [recentRepos, setRecentRepos] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);

  const getActivityIcon = (type) => {
    switch (type) {
      case 'commit': return 'GitCommit';
      case 'star': return 'Star';
      case 'fork': return 'GitFork';
      case 'release': return 'Tag';
      default: return 'Activity';
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'commit': return 'text-green-500';
      case 'star': return 'text-yellow-500';
      case 'fork': return 'text-blue-500';
      case 'release': return 'text-purple-500';
      default: return 'text-muted-foreground';
    }
  };

  const filteredActivity = activityFilter === 'all'
    ? recentActivity
    : recentActivity?.filter(activity => activity?.type === activityFilter);

  useEffect(() => {
    let mounted = true;
    async function fetchStats() {
      try {
        const data = await getGitHubStats();
        if (!mounted || !data) return;

        const totalStars = (data.repositories?.nodes || []).reduce((s, r) => s + (r.stargazerCount || 0), 0);
        const mappedRepos = (data.repositories?.nodes || []).map((r, i) => ({
          id: r.nameWithOwner || `${r.name}-${i}`,
          name: r.name,
          description: r.description || '',
          language: r.primaryLanguage?.name || 'Unknown',
          stars: r.stargazerCount || 0,
          forks: r.forkCount || 0,
          updatedAt: r.pushedAt ? new Date(r.pushedAt).toLocaleString() : 'Unknown',
          visibility: 'public',
          topics: r.repositoryTopics?.nodes?.map(t => t.topic?.name) || [],
        }));

        const languageCount = {};
        mappedRepos.forEach(repo => {
          if (repo.language && repo.language !== 'Unknown') {
            languageCount[repo.language] = (languageCount[repo.language] || 0) + 1;
          }
        });
        skills.forEach(skill => {
          if (!languageCount[skill.name]) {
            languageCount[skill.name] = 1;
          }
        });
        const totalLang = Object.values(languageCount).reduce((a, b) => a + b, 0);
        const languageStats = Object.entries(languageCount)
          .map(([name, count]) => ({
            name,
            count,
            percentage: Math.round((count / totalLang) * 100),
            color:
              name === 'JavaScript' ? '#f7df1e'
              : name === 'TypeScript' ? '#3178c6'
              : name === 'Python' ? '#3776ab'
              : name === 'CSS' ? '#1572b6'
              : name === 'HTML' ? '#e34c26'
              : name === 'Solidity' ? '#3c3c3d'
              : name === 'Go' ? '#00ADD8'
              : name === 'C++' ? '#00599C'
              : name === 'C#' ? '#178600'
              : name === 'Java' ? '#b07219'
              : name === 'Dockerfile' ? '#384d54'
              : name === 'Shell' ? '#89e051'
              : name === 'Ruby' ? '#701516'
              : name === 'PHP' ? '#4F5D95'
              : name === 'Kotlin' ? '#A97BFF'
              : name === 'Swift' ? '#F05138'
              : name === 'Rust' ? '#dea584'
              : name === 'Scala' ? '#c22d40'
              : name === 'Perl' ? '#0298c3'
              : name === 'Dart' ? '#00B4AB'
              : name === 'Objective-C' ? '#438eff'
              : name === 'Jupyter Notebook' ? '#DA5B0B'
              : name === 'GraphQL' ? '#e10098'
              : name === 'Node.js' ? '#68A063'
              : name === 'AWS' ? '#FF9900'
              : name === 'CI/CD' ? '#0A0A0A'
              : name === 'AI/ML' ? '#FFB300'
              : name === 'Open Source' ? '#24292e'
              : '#6b7280'
          }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 6);

        setGitHubStats((prev) => ({
          ...prev,
          totalRepos: data.repositories?.totalCount ?? prev.totalRepos,
          totalStars,
          totalCommits: data.contributionsCollection?.totalCommitContributions ?? prev.totalCommits,
          languageStats,
        }));
        setRecentRepos(mappedRepos);

        const activity = [];
        mappedRepos.forEach((repo, idx) => {
          activity.push({
            id: `${idx}-commit`,
            type: 'commit',
            repo: repo.name,
            message: repo.description ? repo.description : `Recent commit to ${repo.name}`,
            timestamp: repo.updatedAt,
            additions: null,
            deletions: null,
          });
          if (repo.stars > 0) {
            activity.push({
              id: `${idx}-star`,
              type: 'star',
              repo: repo.name,
              message: 'Starred by users',
              timestamp: repo.updatedAt,
            });
          }
          if (repo.forks > 0) {
            activity.push({
              id: `${idx}-fork`,
              type: 'fork',
              repo: repo.name,
              message: 'Forked by users',
              timestamp: repo.updatedAt,
            });
          }
        });
        setRecentActivity(activity);
      } catch (err) {
        console.warn('Failed to fetch GitHub stats for home:', err?.message || err);
      }
    }

    fetchStats();
    return () => { mounted = false; };
  }, []);

  return (
    <section className="py-20 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">GitHub Activity</h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">Real-time insights into my development journey, showcasing active contributions and open-source projects.</p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <motion.div className="lg:col-span-1" initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
            <div className="bg-card rounded-2xl p-8 shadow-brand border border-border">
              <div className="flex items-center space-x-3 mb-8">
                <Icon name="Github" size={24} className="text-foreground" />
                <h3 className="text-xl font-bold text-foreground">GitHub Stats</h3>
              </div>
              <div className="grid grid-cols-2 gap-6 mb-8">
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary mb-1">{githubStats?.totalRepos}</div>
                  <div className="text-sm text-muted-foreground">Repositories</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary mb-1">{githubStats?.totalStars}</div>
                  <div className="text-sm text-muted-foreground">Stars Earned</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary mb-1">{githubStats?.totalCommits}</div>
                  <div className="text-sm text-muted-foreground">Total Commits</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-primary mb-1">{githubStats?.contributionStreak}</div>
                  <div className="text-sm text-muted-foreground">Day Streak</div>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-4 uppercase tracking-wide">Language Distribution</h4>
                <div className="space-y-3">
                  {githubStats?.languageStats?.map((lang, index) => (
                    <motion.div key={lang?.name} className="flex items-center space-x-3" initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: index * 0.1 }} viewport={{ once: true }}>
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: lang?.color }} />
                      <div className="flex-1 flex items-center justify-between">
                        <span className="text-sm font-medium text-foreground">{lang?.name}</span>
                        <span className="text-sm text-muted-foreground">{lang?.percentage}%</span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div className="lg:col-span-2" initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
            <div className="bg-card rounded-2xl p-8 shadow-brand border border-border">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-xl font-bold text-foreground">Recent Repositories</h3>
                <Icon name="ExternalLink" size={20} className="text-muted-foreground hover:text-primary cursor-pointer transition-brand" />
              </div>
              <div className="space-y-6">
                {recentRepos?.map((repo, index) => (
                  <motion.div
                    key={repo?.id}
                    className="group cursor-pointer rounded-xl border border-border p-6 transition-brand hover:border-primary/30 hover:bg-muted/30"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    onClick={() => setSelectedRepo(selectedRepo === repo?.id ? null : repo?.id)}
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <Icon name="Folder" size={20} className="text-primary" />
                        <h4 className="font-semibold text-foreground transition-brand group-hover:text-primary">{repo?.name}</h4>
                        {!repo?.isPrivate && <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800">Public</span>}
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                        <div className="flex items-center space-x-1"><Icon name="Star" size={14} /><span>{repo?.stars}</span></div>
                        <div className="flex items-center space-x-1"><Icon name="GitFork" size={14} /><span>{repo?.forks}</span></div>
                      </div>
                    </div>
                    <p className="text-muted-foreground mb-4 leading-relaxed">{repo?.description}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2"><div className="h-3 w-3 rounded-full bg-primary" /><span className="text-sm text-muted-foreground">{repo?.language}</span></div>
                        <span className="text-sm text-muted-foreground">Updated {repo?.updatedAt}</span>
                      </div>
                    </div>
                    <motion.div
                      className="mt-4 flex flex-wrap gap-2"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: selectedRepo === repo?.id ? 1 : 0, height: selectedRepo === repo?.id ? 'auto' : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      {repo?.topics?.map((topic) => (
                        <span key={topic} className="rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary">{topic}</span>
                      ))}
                    </motion.div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>

        <motion.div className="mt-12" initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}>
          <div className="bg-card rounded-2xl border border-border p-8 shadow-brand">
            <div className="flex items-center justify-between mb-8">
              <h3 className="text-xl font-bold text-foreground">Recent Activity</h3>
              <div className="flex items-center space-x-2">
                {['all', 'commit', 'star', 'fork', 'release']?.map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setActivityFilter(filter)}
                    className={`rounded-full px-3 py-1 text-sm font-medium transition-brand capitalize ${
                      activityFilter === filter ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    {filter}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-4">
              {filteredActivity?.map((activity, index) => (
                <motion.div
                  key={activity?.id}
                  className="flex items-start space-x-4 rounded-lg p-4 transition-brand hover:bg-muted/30"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className={`rounded-full bg-muted p-2 ${getActivityColor(activity?.type)}`}>
                    <Icon name={getActivityIcon(activity?.type)} size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="font-medium text-foreground">{activity?.repo}</span>
                      <span className="text-muted-foreground">•</span>
                      <span className="text-sm text-muted-foreground">{activity?.timestamp}</span>
                    </div>
                    <p className="text-muted-foreground">{activity?.message}</p>
                    {activity?.additions && activity?.deletions && (
                      <div className="mt-2 flex items-center space-x-4 text-sm">
                        <span className="text-green-500">+{activity?.additions}</span>
                        <span className="text-red-500">-{activity?.deletions}</span>
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default GitHubActivity;
